import parsePhoneNumberFromString, { getCountryCallingCode } from 'libphonenumber-js';

export function dialDigitsFromCountry(country) {
    if (!country) return '';
    const fromPhoneCode = String(country.phone_code ?? '').replace(/\D/g, '');
    if (fromPhoneCode) return fromPhoneCode;

    const iso = resolveCountryIso(country);
    if (!iso) return '';
    try {
        return String(getCountryCallingCode(iso));
    } catch (_) {
        return '';
    }
}

export function resolveCountryIso(country) {
    if (!country) return '';
    return String(country.country_code ?? country.code ?? '').trim().toUpperCase();
}

/**
 * Build E.164 digits (no leading +) for tel: / wa.me from stored value + entity country.
 * Does not mutate stored phone; only used when building links.
 */
export function resolveInternationalPhoneDigits(phone, { country, dialCode, countryIso } = {}) {
    if (phone == null) return '';

    const dial = String(dialCode ?? dialDigitsFromCountry(country) ?? '').replace(/\D/g, '');
    const iso = countryIso || resolveCountryIso(country);

    let raw = String(phone).trim();
    if (!raw) return '';

    if (/^tel:/i.test(raw)) {
        raw = raw.slice(4).trim();
    }

    raw = raw.replace(/[\s\-().]/g, '');

    if (raw.startsWith('00')) {
        raw = `+${raw.slice(2)}`;
    }

    const explicitInternational = raw.startsWith('+');
    const digitsOnly = raw.replace(/\D/g, '');
    if (!digitsOnly) return '';

    if (explicitInternational) {
        const parsed = parsePhoneNumberFromString(raw);
        if (parsed?.isValid()) return parsed.number.replace(/^\+/, '');
        return digitsOnly;
    }

    if (dial && digitsOnly.startsWith(dial) && digitsOnly.length > dial.length) {
        const withDial = parsePhoneNumberFromString(`+${digitsOnly}`);
        if (withDial?.isValid()) return withDial.number.replace(/^\+/, '');
        return digitsOnly;
    }

    const national = digitsOnly.replace(/^0+/, '');
    if (dial && iso) {
        const composed = parsePhoneNumberFromString(`+${dial}${national}`, iso);
        if (composed?.isValid()) return composed.number.replace(/^\+/, '');
    }

    if (dial) return `${dial}${national}`;
    return national;
}

export function toTelHrefForContact(phone, countryContext) {
    const digits = resolveInternationalPhoneDigits(phone, countryContext);
    if (!digits) return '';
    return `tel:+${digits}`;
}

export function toWaMeHref(phone, countryContext, message) {
    const digits = resolveInternationalPhoneDigits(phone, countryContext);
    if (!digits) return '';
    const base = `https://wa.me/${digits}`;
    if (message == null || message === '') return base;
    return `${base}?text=${encodeURIComponent(message)}`;
}

export function countryContextFromStore(storeData, countries = []) {
    const country = storeData?.country;
    if (!country) return {};

    const match = countries.find((item) => String(item.id) === String(country.id));
    return {
        country: {
            ...country,
            phone_code: country.phone_code || (match?.dial ? `+${match.dial}` : ''),
            country_code: country.country_code || country.code || match?.iso || '',
        },
    };
}
